import * as React from 'react';
import { useLocation } from 'react-router';
import type { PiletApi } from '../index.ts';
import type { NotificationViews } from '../host/react.tsx';

// The frame, as the Piral shell drew it: a navigation rail on the left, the
// current screen beside it, and a bar at the bottom with a status slot.
// There is no top bar. The rail is a slot the rail plugin fills; screens
// contribute their entries to it. A screen that wants the whole viewport,
// such as the landing page, marks its route by contributing `{ path }` to
// `shell.chromeless`; the frame then draws neither the rail nor the bottom
// bar there. How it looks is the theme plugin's; what goes in it is other
// plugins'.

export const NotFound: React.FC = () => <p className="error">Not found.</p>;

/** A route is chromeless when a `shell.chromeless` contribution names it, exactly or as a prefix. */
function isChromeless(pathname: string, contributions: React.ReactNode): boolean {
  return React.Children.toArray(contributions).some((child) => {
    const path = (child as React.ReactElement).props?.params?.path;
    return typeof path === 'string' && (pathname === path || pathname.startsWith(`${path}/`));
  });
}

export const Frame: React.FC<{ Extension: PiletApi['Extension']; notifications: React.ReactNode; children: React.ReactNode }> = ({
  Extension,
  notifications,
  children,
}) => {
  const { pathname } = useLocation();
  return (
    <div className="frame">
      {/* The gate: plugins that must act before any screen shows, such as a
          redirect to the landing page while no account is set. Draws nothing. */}
      <Extension name="shell.gate" />
      {/* Overlays over every screen, such as the help dialog (plugin
          shared-help); each draws nothing until it is opened. */}
      <Extension name="shell.overlays" />
      <Extension
        name="shell.chromeless"
        render={(contributions) => {
          const bare = isChromeless(pathname, contributions);
          return (
            <>
              <div className="frame-main">
                {/* The rail: filled by the rail plugin; nothing is drawn here without it. */}
                {!bare && <Extension name="shell.rail" />}
                <main className="frame-body">{children}</main>
              </div>
              {/* Toasts, shown by the toast plugin; styled by the theme. */}
              {notifications}
            </>
          );
        }}
      />
    </div>
  );
};

export const notificationViews: NotificationViews = {
  Host: ({ children }) => (
    <div className="notifications" role="status" aria-live="polite">
      {children}
    </div>
  ),
  // One toast: the content comes from the toast plugin, with its own icon and tint.
  Toast: ({ options, onClose, children }) => (
    <div className="toast">
      <div className="toast-body">
        {options.title && <div className="toast-title">{options.title}</div>}
        {children}
      </div>
      <button type="button" className="toast-close" aria-label="Dismiss" onClick={onClose}>
        ×
      </button>
    </div>
  ),
};
